import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { AuthenticationService } from 'app/main/auth/service';
import { CompanySettingService } from 'app/Shared/company-setting.service';
import { menuService } from "app/menu/menu";
import { PermissionService } from '@core/services/permission.service';
import { Features } from '@core/types/features.enum';
import { CoreMenu, CoreMenuItem } from '@core/types';
import { Permissions } from '@core/types/permissions.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthRouteEnum } from 'app/main/auth/controllers/route.enum';
import { LocalStorageKeys } from '@shared/default-values';

@Component({
  selector: '[core-menu]',
  templateUrl: './core-menu.component.html',
  styleUrls: ['./core-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CoreMenuComponent implements OnInit, OnDestroy {
  currentUser: any;

  @Input()
  layout = 'vertical';

  @Input()
  menu: CoreMenu[] = [];
  _modules: string[] = [];
  isEnableEmployeeSelfServicesAccess: boolean = JSON.parse(localStorage.getItem('companySettings'))?.enableEmployeeSelfServicesAccess;
  isEnablePayroll: boolean = JSON.parse(localStorage.getItem('companySettings'))?.enablePayrollAccess;
  isEnableReports: string[] = JSON.parse(localStorage.getItem('companySettings'))?.reports;
  isEnableRequestMissingPunch: boolean = JSON.parse(localStorage.getItem('defaultSetting'))?.enableRequestMissingPunch;

  // Private
  private _unsubscribeAll: Subject<void>;
  companyId: string;

  /**
   *
   * @param {ChangeDetectorRef} _changeDetectorRef
   * @param {CoreMenuService} _coreMenuService
   */
  constructor(private _companySettingService: CompanySettingService, private _authenticationService: AuthenticationService,
    private _changeDetectorRef: ChangeDetectorRef, private _coreMenuService: CoreMenuService, private _permissionService: PermissionService, private _router: Router, private _activeRoute: ActivatedRoute) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Set the menu either from the input or from the service
    this.menu = this.menu || this._coreMenuService.getCurrentMenu();
    this._companySettingService.companySettings$.pipe(takeUntil(this._unsubscribeAll)).subscribe((settings) => {
      if (settings) {
        const menulist = new menuService()
        this._coreMenuService.unregister("main");
        this._coreMenuService.register("main", menulist.menuList);
        this.menu = this._coreMenuService.getCurrentMenu();
        if (this.updateMenu()) {
          this.isEnableEmployeeSelfServicesAccess = JSON.parse(localStorage.getItem('companySettings'))?.enableEmployeeSelfServicesAccess;
          this.isEnablePayroll = JSON.parse(localStorage.getItem('companySettings'))?.enablePayrollAccess;
          this.isEnableReports = JSON.parse(localStorage.getItem('companySettings'))?.reports;
          this.isEnableRequestMissingPunch = JSON.parse(localStorage.getItem('defaultSetting'))?.enableRequestMissingPunch;
          this.hiddenMenuItem();
          this.applyPermission(true);
        }
      }
    });
    // Subscribe to the current menu changes
    this._coreMenuService.onMenuChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
      this.currentUser = this._coreMenuService.currentUser;

      // Load menu
      this.menu = this._coreMenuService.getCurrentMenu();
      this._modules = Object.values(Features);

      this._changeDetectorRef.markForCheck();
    });

    this.hiddenMenuItem();
    this.applyPermission();

    this.getCompany()
  }

  applyPermission(isUpdate: boolean = false) {
    this._permissionService.permissions$.pipe(takeUntil(this._unsubscribeAll)).subscribe((perms: any[]) => {
      setTimeout(() => {
        if (this._permissionService.getIsSuperAdmin()) {
          this.menu.forEach(menuItem => {
            menuItem.hidden = false
            if (menuItem?.children) {
              menuItem.children.forEach(child => {
                child.hidden = false
              })
            }
            if (menuItem?.id == 'payroll-management') {
              if (this.isEnablePayroll) {
                menuItem.hidden = false
              } else {
                menuItem.hidden = true
              }
            }
            if (menuItem.moduleName === Features.REPORT_LINK) {
              if (this.isEnableReports?.length > 0) {
                menuItem.hidden = false
              } else {
                menuItem.hidden = true
              }
            }
          })
        } else {
          const modules: string[] = [...new Set(perms.map(p => p.module))]
          this.menu.forEach(menuItem => {
            if (menuItem.id === 'employee-profile') {
              menuItem.hidden = !this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.READ)
            }
            else if (menuItem.moduleName === Features.EMPLOYEE && (this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.READ) || this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.LEAVE_READ_MY_OWN_LEAVE) || this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.CUSTOM_FIELD_READ))) {
              menuItem.hidden = false
              menuItem.children.forEach(child => {
                if (child.id === 'employee_add' && this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.ADD)) {
                  child.hidden = false
                } else if (child.id === 'leaves' && this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.LEAVE_READ_MY_OWN_LEAVE)) {
                  child.hidden = false
                } else if (child.id === 'employee-custom-field' && this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.CUSTOM_FIELD_READ)) {
                  child.hidden = false
                } else if (child.id === 'employee-group' && this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.CUSTOM_FIELD_READ)) {
                  child.hidden = false
                }
                else if ((child.id === 'employees_search' || child.id === 'employee-profile') && this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.READ)) {
                  child.hidden = false
                }
              })
            }
            else if (menuItem.moduleName === Features.ROSTER && this._permissionService.hasPermission(Features.ROSTER, Permissions.ROSTER.READ)) {
              menuItem.hidden = false
              menuItem.children.forEach(child => {
                if (child.id === 'list_roster') {
                  child.hidden = false
                } else if (child.id === 'add_roster' && this._permissionService.hasPermission(Features.ROSTER, Permissions.ROSTER.ADD)) {
                  child.hidden = false
                }
              })
            }
            else if (menuItem.moduleName === Features.USERS && this._permissionService.hasPermission(Features.USERS, Permissions.USERS.VIEW)) {
              menuItem.hidden = false
              menuItem.children.forEach(child => {
                if (child.id === 'list_user' && this._permissionService.hasPermission(Features.USERS, Permissions.USERS.VIEW)) {
                  child.hidden = false
                }
              })
            }
            else if (menuItem.moduleName === Features.TIME_MANAGEMENT) {
              menuItem.hidden = false
              menuItem.children.forEach(child => {
                if (child.id === 'time_management_manage' && this._permissionService.hasPermission(Features.TIME_MANAGEMENT, Permissions.TIME_MANAGEMENT.READ)) {
                  child.hidden = false  // time sheet
                } else if (child.id === 'time_management_night_audit' && this._permissionService.hasPermission(Features.TIME_MANAGEMENT, Permissions.TIME_MANAGEMENT.NIGHT_AUDIT)) {
                  child.hidden = false  // NIGHT_AUDIT
                } else if (child.id === 'time_management_upload_sheet' && this._permissionService.hasPermission(Features.TIME_MANAGEMENT, Permissions.TIME_MANAGEMENT.UPLOAD_TIME_SHEET)) {
                  child.hidden = false  // UPLOAD_TIME_SHEET
                } else if (child.id === 'time_management_attendence_sheet' && this._permissionService.hasPermission(Features.TIME_MANAGEMENT, Permissions.TIME_MANAGEMENT.ATTENDANCE_RECORDS_VIEW_MY_OWN)) {
                  child.hidden = false  // ATTENDANCE_RECORDS
                } else if (child.id === 'pull-time-employee' && this._permissionService.hasPermission(Features.TIME_MANAGEMENT, Permissions.TIME_MANAGEMENT.PULL_EMPLOYEE_TIMES)) {
                  child.hidden = false  // ATTENDANCE_RECORDS
                } else if (child.id === 'check-in-out') {
                  child.hidden = false
                }
              })
            }
            else if (menuItem.moduleName === Features.REFERENCES) {
              const setupModules = [
                Features.REFERENCES_DEFAULT_SETTING, Features.REFERENCES_BUSINESS_RULES, Features.JOB_CODES,
                Features.HOLIDAYS, Features.REFERENCES_DEPARTMENT, Features.GROUPS, Features.EMPLOYEE_GROUP,
                Features.JOB_TITLES, Features.ATTACH_CATEGORIES, Features.LEAVE_REASONS, Features.ROSTER_TIMES,
                Features.TIME_RECORD_TAGS, Features.TAG_GROUPS, Features.REFERENCES_ALL_OTHERS,
                Features.PAYROLL_ACCOUNTS, Features.BANKS, Features.BANK_BRANCHES, Features.RATE_TYPE,
              ];
              menuItem.hidden = !setupModules.some(m => modules.includes(m));
            }
            else if (menuItem.moduleName === Features.REQUESTS && this.isEnableEmployeeSelfServicesAccess) {
              menuItem.hidden = false
              menuItem.children.forEach(child => {
                if (this.isEnableRequestMissingPunch && child.id === 'addtional-request' && (this._permissionService.hasPermission(Features.REQUESTS, Permissions.REQUESTS.ADDITIONAL_REQUEST_ADD) || this._permissionService.hasPermission(Features.REQUESTS, Permissions.REQUESTS.ADDITIONAL_REQUEST_ADD_MY_OWN))) {
                  child.hidden = false
                } else if (child.id == 'request-leave' && this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.LEAVE_ADD_MY_OWN_LEAVE)) {
                  child.hidden = false
                } else if (child.id === 'request-leave' && !this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.LEAVE_ADD_MY_OWN_LEAVE)) {
                  child.hidden = true
                } else if (child.id == 'view all' && (this._permissionService.hasPermission(Features.REQUESTS, Permissions.REQUESTS.ADDITIONAL_REQUEST_READ_MY_OWN) || this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.LEAVE_READ_MY_OWN_LEAVE))) {
                  child.hidden = false
                } else if (child.id == 'approval-requests' && (this._permissionService.hasPermission(Features.REQUESTS, Permissions.REQUESTS.ADDITIONAL_REQUEST_READ_MY_OWN) || this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.LEAVE_READ_MY_OWN_LEAVE))) {
                  child.hidden = false
                } else if (child.id === 'addtional-request' && !this._permissionService.hasPermission(Features.REQUESTS, Permissions.REQUESTS.ADDITIONAL_REQUEST_ADD) && !this._permissionService.hasPermission(Features.REQUESTS, Permissions.REQUESTS.ADDITIONAL_REQUEST_ADD_MY_OWN)) {
                  child.hidden = true
                }
              })
            }
            else if (menuItem.id === 'payroll-management' && this.isEnablePayroll) {
              menuItem.hidden = false
              menuItem.children.forEach(child => {
                if (child.id === 'upload_auto_transactions' && this._permissionService.hasPermission(Features.EMPLOYEE, Permissions.EMPLOYEE.AUTO_TRANSACTION_READ)) {
                  child.hidden = false
                }
                // else if (child.id === 'account' && this._permissionService.hasPermission(Features.PAYROLL_ACCOUNTS, Permissions.PAYROLL_ACCOUNTS.READ)) {
                //   child.hidden = false
                // }
                // else if (child.id === 'rate-types' && this._permissionService.hasPermission(Features.RATE_TYPE, Permissions.RATE_TYPE.READ)) {
                //   child.hidden = false
                // }
                else if (child.id === 'service-charge' && this._permissionService.hasPermission(Features.PAYROLL, Permissions.PAYROLL.Service_Charges_View)) {
                  child.hidden = false
                }
                else if (child.id === 'vacation-pay' && this._permissionService.hasPermission(Features.VACATION_PAY, Permissions.VACATION_PAY.READ)) {
                  child.hidden = false
                }
                else if (child.id === 'severance-pay' && this._permissionService.hasPermission(Features.SEVERANCE_PAY, Permissions.SEVERANCE_PAY.READ)) {
                  child.hidden = false
                }
              })
            }
            else if (menuItem.id === 'administration' && this.hasPermissionAdministration()) {
              menuItem.hidden = false
              menuItem.children.forEach(child => {
                if (child.id === 'companies' && this._permissionService.hasPermission(Features.COMPANIES, Permissions.COMPANIES.READ)) {
                  child.hidden = false
                }
                else if (child.id === 'administration_users' && this._permissionService.hasPermission(Features.LOCAL_ADMINS, Permissions.LOCAL_ADMINS.READ)) {
                  child.hidden = false
                }
                else if (child.id === 'roles' && this._permissionService.hasPermission(Features.ROLES, Permissions.ROLES.READ)) {
                  child.hidden = false
                }
                else if (child.id === 'upload-employees' && this._permissionService.hasPermission(Features.UPLOAD_EMPLOYEES, Permissions.UPLOAD_EMPLOYEES.DOWNLOAD_TEMPLATE)) {
                  child.hidden = false
                }
                else if (child.id === 'file_types' && this._permissionService.hasPermission(Features.FILE_TYPES, Permissions.FILE_TYPES.READ)) {
                  child.hidden = false
                }
              })
            } else if (menuItem.moduleName === Features.REPORT_LINK && this.isEnableReports?.length > 0 && this._permissionService.hasPermission(Features.REPORT_LINK, Permissions.REPORT_LINK.READ)) {
              menuItem.hidden = false
            }
            else if (menuItem.moduleName === Features.Dashboard) {
              menuItem.hidden = !this._permissionService.hasPermission(Features.Dashboard, Permissions.Dashboard.READ)
            }
            else {
              menuItem.hidden = !modules.includes(menuItem?.moduleName)
            }
          })

          // Hide any collapsible parent that has no visible children.
          this.menu.forEach(menuItem => {
            if (menuItem.children && menuItem.children.length > 0) {
              menuItem.hidden = !menuItem.children.some(child => !child.hidden);
            }
          })
        }
        if (isUpdate) {
          this._changeDetectorRef.markForCheck();
          return;
        }
        let stop = false
        Object.values(AuthRouteEnum).forEach(route => {
          if (window.location.pathname === route) {
            stop = true
          }
        });
        if (window.location.pathname.split('/')[1] == 'reports') {
          stop = true
        }

        if (stop) {
          this._changeDetectorRef.markForCheck();
          return
        }
        const path = window.location.pathname;
        const isRoot = path === '/' || path === '';

        // A real URL is being loaded (deep-link, manual entry, duplicate tab, refresh) —
        // respect it instead of force-navigating away.
        if (!isRoot) {
          this._changeDetectorRef.markForCheck();
          return;
        }

        // Bare root only: SuperAdmin without a selected company must choose one first.
        if (this._permissionService.getIsSuperAdmin() && !this.companyId) {
          this._changeDetectorRef.markForCheck();
          return;
        }

        // All other users land on the shared home page.
        this._router.navigate(['/home']);
        this._changeDetectorRef.markForCheck();
      }, 100)
    })
  }

  updateMenu(): boolean {
    if (this.isEnableReports != JSON.parse(localStorage.getItem('companySettings'))?.reports ||
      this.isEnablePayroll != JSON.parse(localStorage.getItem('companySettings'))?.enablePayrollAccess ||
      this.isEnableEmployeeSelfServicesAccess != JSON.parse(localStorage.getItem('companySettings'))?.enableEmployeeSelfServicesAccess ||
      this.isEnableRequestMissingPunch != JSON.parse(localStorage.getItem('defaultSetting'))?.enableRequestMissingPunch) {
      return true;
    }
    return false
  }

  getCompany() {
    if (this._authenticationService.currentUser) {
      var company = this._authenticationService.currentUser.companyId;
      if (company != null && company != undefined && company != "" && company != '00000000-0000-0000-0000-000000000000') {
        this.companyId = company;

      } else {
        this.companyId = null;
      }
    }
  }


  findFirstAccessibleRoute(): string | null {
    // Helper function to recursively find the first accessible menu item with a URL
    const findFirstAccessibleItem = (menuItems: CoreMenuItem[]): CoreMenuItem | null => {
      for (const item of menuItems) {
        if (item.hidden) {
          continue;
        };

        if (item.url && !item.hidden) {
          return item;
        }

        if (item.children && item.children.length > 0) {
          const childResult = findFirstAccessibleItem(item.children);
          if (childResult) {
            return childResult;
          }
        }
      }
      return null;
    };

    const firstAccessibleItem = findFirstAccessibleItem(this.menu);
    return firstAccessibleItem ? firstAccessibleItem.url : null;
  }


  accessToLastVisitedPage(): boolean {
    const lastVisitedPage = localStorage.getItem(LocalStorageKeys.lastVisitedUrl)?.slice(1) ?? null;
    setTimeout(() => {
      localStorage.removeItem(LocalStorageKeys.lastVisitedUrl);
    }, 2000);

    if (!lastVisitedPage) return false;
    if (lastVisitedPage && lastVisitedPage != '/pages/miscellaneous/error' && !lastVisitedPage.includes('auth')) {
      this._router.navigateByUrl(lastVisitedPage);
      return true;
    }
    return false;
  }

  hiddenMenuItem() {
    this.menu.forEach(menuItem => {
      menuItem.hidden = true;
      if (menuItem?.children) {
        menuItem.children.forEach(child => {
          if (child.id != 'check-in-out') {
            child.hidden = true;
          }
        });
      }
    });
  }

  hasPermissionAdministration(): boolean {
    return (
      this._permissionService.hasPermission(Features.FILE_TYPES, Permissions.FILE_TYPES.READ) ||
      this._permissionService.hasPermission(Features.LOCAL_ADMINS, Permissions.LOCAL_ADMINS.READ) ||
      this._permissionService.hasPermission(Features.ROLES, Permissions.ROLES.READ) ||
      this._permissionService.hasPermission(Features.UPLOAD_EMPLOYEES, Permissions.UPLOAD_EMPLOYEES.DOWNLOAD_TEMPLATE) ||
      this._permissionService.hasPermission(Features.COMPANIES, Permissions.COMPANIES.READ)
    );
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

}


